import type { CapturedPhotoAsset } from '@/shared/camera/photo-capture-adapter';
import {
  PHOTO_QUALITY_STATUSES,
  type PhotoQualityIssue,
  type PhotoQualityResult,
  VALIDATION_SEVERITIES,
} from '@/shared/capture/capture-session';

const MIN_USEFUL_EDGE_PX = 640;
const MIN_EXPECTED_SIZE_BYTES = 1_024;

export function evaluateCapturedPhotoQuality(
  asset: CapturedPhotoAsset,
  checkedAt = new Date().toISOString(),
): PhotoQualityResult {
  const issues: PhotoQualityIssue[] = [];

  if (!asset.mimeType.startsWith('image/')) {
    issues.push({
      code: 'PHOTO_MIME_NOT_IMAGE',
      message: 'Captured file is not an image.',
      severity: VALIDATION_SEVERITIES.warning,
    });
  }

  if (asset.width <= 0 || asset.height <= 0) {
    issues.push({
      code: 'PHOTO_DIMENSIONS_UNAVAILABLE',
      message: 'Photo dimensions could not be read on this device.',
      severity: VALIDATION_SEVERITIES.warning,
    });
  } else if (Math.min(asset.width, asset.height) < MIN_USEFUL_EDGE_PX) {
    issues.push({
      code: 'PHOTO_LOW_RESOLUTION',
      message: 'Photo resolution may be too low for OCR and review.',
      severity: VALIDATION_SEVERITIES.warning,
    });
  }

  if (asset.originalBlob.size < MIN_EXPECTED_SIZE_BYTES) {
    issues.push({
      code: 'PHOTO_FILE_SMALL',
      message: 'Photo file is unusually small.',
      severity: VALIDATION_SEVERITIES.warning,
    });
  }

  return {
    checkedAt,
    issues,
    status: issues.length > 0 ? PHOTO_QUALITY_STATUSES.warning : PHOTO_QUALITY_STATUSES.ok,
  };
}
